// src/components/artwork/ApiTagsGeneratorWithLimits.jsx
import React, { useState, useEffect } from "react";
import apiUsageManager from "./ApiUsageManager";
import ApiUsageStatus from "./ApiUsageStatus";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiBase";

// ---------------- Permissions tags / plans ----------------

function getTagPermissions(profile) {
  const planRaw =
    (profile?.subscription_plan ||
      profile?.plan ||
      profile?.currentPlan ||
      "") + "";
  const roleRaw = (profile?.user_role || profile?.role || "") + "";

  const plan = planRaw.toLowerCase();
  const role = roleRaw.toLowerCase();

  const isFamilyOffice =
    role === "family_office" ||
    role === "familyoffice" ||
    plan.includes("family");
  const isAdvisor =
    role === "advisor" ||
    role === "art_advisor" ||
    plan.includes("advisor");
  const isPremium =
    plan.includes("premium") || plan.includes("prm") || plan.includes("plus");

  // Standard user        -> manuel (pas d’auto)
  // Premium user         -> auto + manuel
  // Art Advisor (std/pr) -> auto + manuel
  // Family Office        -> vue seule (pas d’auto ni manuel)
  const canUseAuto = !isFamilyOffice && (isPremium || isAdvisor);
  const canEditManual = !isFamilyOffice;

  return { canUseAuto, canEditManual, isFamilyOffice };
}

// ---------------- Constantes de catégories ----------------

const TAG_CATEGORIES = {
  MEDIUM: "Médium",
  TECHNIQUE: "Technique",
  STYLE: "Style/Mouvement",
  THEME: "Thème",
  SUBJECT: "Sujet",
  ERA: "Période",
  MATERIAL: "Matériau",
  COLOR: "Couleur",
  TECHNOLOGY: "Technologie",
};

// Nouvelles catégories de tags interdisciplinaires
const INTERDISCIPLINARY_CATEGORIES = {
  MUSICAL: "Thème musical",
  LITERARY: "Référence littéraire",
  VISUAL_ANALYSIS: "Analyse visuelle",
  CULTURAL_REFERENCE: "Référence culturelle",
  EXHIBITION: "Exposition/Présentation",
  COMPOSITION: "Composition/Structure",
  CONCEPTUAL: "Concept/Idée",
};

// 🔧 Mots-clés utilisés par l’analyse locale pour remplir les catégories
const INTERDISCIPLINARY_KEYWORDS = {
  MUSICAL: [
    "symphonie",
    "concerto",
    "quatuor",
    "sonate",
    "musique",
    "partition",
    "orchestre",
    "haydn",
    "mozart",
    "beethoven",
  ],
  LITERARY: [
    "roman",
    "poème",
    "poésie",
    "récit",
    "texte",
    "écrivain",
    "auteur",
    "littéraire",
  ],
  VISUAL_ANALYSIS: [
    "constellation",
    "composition",
    "structure",
    "rythme visuel",
    "géométrie",
    "abstrait",
    "abstraction",
  ],
  CULTURAL_REFERENCE: [
    "mythologie",
    "mythique",
    "biblique",
    "culturel",
    "référence",
    "cinéma",
    "film",
    "musée",
  ],
  EXHIBITION: [
    "exposition",
    "biennale",
    "triennale",
    "galerie",
    "musée",
    "maison européenne de la photographie",
    "mep",
  ],
  COMPOSITION: [
    "diptyque",
    "triptyque",
    "polyptyque",
    "série",
    "ensemble",
    "installation",
  ],
  CONCEPTUAL: [
    "concept",
    "idée",
    "interprétation",
    "système",
    "conceptuel",
    "démarche",
  ],
};

// Catégories Premium exclusives
const PREMIUM_CATEGORIES = {
  MARKET: "Marché",
  INFLUENCE: "Influences",
  CULTURAL_CONTEXT: "Contexte culturel",
  CONSERVATION: "Conservation",
  EXHIBITION_HISTORY: "Historique d'exposition",
};

// Noms propres et termes spécifiques
const SPECIFIC_TERMS = [
  {
    term: "haydn",
    tag: "joseph haydn",
    category: INTERDISCIPLINARY_CATEGORIES.MUSICAL,
  },
  {
    term: "mozart",
    tag: "wolfgang amadeus mozart",
    category: INTERDISCIPLINARY_CATEGORIES.MUSICAL,
  },
  {
    term: "beethoven",
    tag: "ludwig van beethoven",
    category: INTERDISCIPLINARY_CATEGORIES.MUSICAL,
  },
  {
    term: "opus",
    tag: "opus musical",
    category: INTERDISCIPLINARY_CATEGORIES.MUSICAL,
  },
  {
    term: "quatuor",
    tag: "quatuor",
    category: INTERDISCIPLINARY_CATEGORIES.MUSICAL,
  },
  {
    term: "maison européenne de la photographie",
    tag: "maison européenne de la photographie",
    category: INTERDISCIPLINARY_CATEGORIES.EXHIBITION,
  },
  {
    term: "mep",
    tag: "maison européenne de la photographie",
    category: INTERDISCIPLINARY_CATEGORIES.EXHIBITION,
  },
  {
    term: "constellation",
    tag: "constellation",
    category: INTERDISCIPLINARY_CATEGORIES.VISUAL_ANALYSIS,
  },
  {
    term: "tirage",
    tag: "tirage photographique",
    category: TAG_CATEGORIES.TECHNIQUE,
  },
  {
    term: "photographique",
    tag: "photographie",
    category: TAG_CATEGORIES.MEDIUM,
  },
];

// ---------------- Composant principal ----------------

/**
 * Composant d'analyse de description d'œuvre d'art utilisant l'API OpenAI
 * avec gestion des limitations d'utilisation et analyse interdisciplinaire améliorée.
 */
const ApiTagsGenerator = ({
  description,
  currentTags,
  setTags,
  apiKey,
  apiEndpoint,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestedTags, setSuggestedTags] = useState({});
  const [selectedTags, setSelectedTags] = useState(currentTags || []);
  const [lastAnalyzedText, setLastAnalyzedText] = useState("");
  const [usageStats, setUsageStats] = useState(apiUsageManager.getUsageStats());
  const [showManualAnalysisButton, setShowManualAnalysisButton] =
    useState(true);
  const [localAnalysisDone, setLocalAnalysisDone] = useState(false);

  const { profile } = useAuth() || {};
  const { canUseAuto, canEditManual, isFamilyOffice } =
    getTagPermissions(profile);

  const canCallApi =
    usageStats.remainingCalls > 0 || apiEndpoint === "mock";

  // ---------- Analyse locale ----------
  const performLocalAnalysis = (text) => {
    if (!text || text.trim().length < 20) return {};

    const normalizedText = text.toLowerCase();
    const localTags = {};

    // Interdisciplinaire via dictionnaire de mots-clés
    Object.entries(INTERDISCIPLINARY_KEYWORDS).forEach(
      ([categoryKey, keywords]) => {
        const foundKeywords = keywords
          .filter((keyword) => normalizedText.includes(keyword))
          .slice(0, 5);

        if (foundKeywords.length > 0) {
          const categoryName =
            INTERDISCIPLINARY_CATEGORIES[categoryKey] || categoryKey;
          localTags[categoryName] = foundKeywords;
        }
      }
    );

    // Termes spécifiques
    SPECIFIC_TERMS.forEach(({ term, tag, category }) => {
      if (normalizedText.includes(term)) {
        if (!localTags[category]) {
          localTags[category] = [];
        }
        if (!localTags[category].includes(tag)) {
          localTags[category].push(tag);
        }
      }
    });

    // Dimensions
    const dimensionMatch = normalizedText.match(/\((\d+)[^\d]+(\d+)[^)]+\)/);
    if (dimensionMatch) {
      if (!localTags[INTERDISCIPLINARY_CATEGORIES.VISUAL_ANALYSIS]) {
        localTags[INTERDISCIPLINARY_CATEGORIES.VISUAL_ANALYSIS] = [];
      }
      localTags[INTERDISCIPLINARY_CATEGORIES.VISUAL_ANALYSIS].push(
        "format spécifique"
      );
    }

    // Séries / multiples
    if (
      normalizedText.match(/\d+\s*x\s*\d+/) ||
      normalizedText.includes("série") ||
      normalizedText.includes("multiple") ||
      normalizedText.includes("tirages")
    ) {
      if (!localTags[INTERDISCIPLINARY_CATEGORIES.VISUAL_ANALYSIS]) {
        localTags[INTERDISCIPLINARY_CATEGORIES.VISUAL_ANALYSIS] = [];
      }
      localTags[INTERDISCIPLINARY_CATEGORIES.VISUAL_ANALYSIS].push(
        "œuvre en série"
      );
    }

    // Médium basique
    if (
      normalizedText.includes("photographie") ||
      normalizedText.includes("photographique")
    ) {
      if (!localTags[TAG_CATEGORIES.MEDIUM]) {
        localTags[TAG_CATEGORIES.MEDIUM] = ["photographie"];
      }
    }

    return localTags;
  };

  // ---------- Analyse avec API ----------
  const analyzeWithAPI = async (text, forceAnalysis = false) => {
    if (!canUseAuto) return;
    if (!text || text.trim().length < 20) return;
    if (text === lastAnalyzedText && !forceAnalysis) return;

    const localResults = performLocalAnalysis(text);
    setLocalAnalysisDone(true);

    // Pas d’API → résultats locaux seulement
    if ((apiEndpoint === "mock" || !canCallApi) && !forceAnalysis) {
      setSuggestedTags(localResults);
      setLastAnalyzedText(text);
      setIsLoading(false);
      return localResults;
    }

    // Cache
    const cachedResult = apiUsageManager.getCachedResult(text);
    if (cachedResult && !forceAnalysis) {
      const mergedResults = { ...localResults, ...cachedResult };
      setSuggestedTags(mergedResults);
      setLastAnalyzedText(text);
      return mergedResults;
    }

    if (!canCallApi && apiEndpoint !== "mock") {
      setError("Vous avez atteint votre limite mensuelle d'analyses.");
      setSuggestedTags(localResults);
      return localResults;
    }

    setIsLoading(true);
    setError(null);

    try {
      const isPremium = apiUsageManager.isPremium();
      const categoriesForPrompt = isPremium
        ? {
            ...TAG_CATEGORIES,
            ...INTERDISCIPLINARY_CATEGORIES,
            ...PREMIUM_CATEGORIES,
          }
        : { ...TAG_CATEGORIES, ...INTERDISCIPLINARY_CATEGORIES };


      const prompt = `
Analysez cette description d'œuvre d'art et générez des mots-clés pertinents organisés par catégorie.
Soyez particulièrement attentif aux aspects interdisciplinaires (musique, littérature, etc.)
et aux références culturelles spécifiques.

Description: "${text}"

Format de réponse souhaité:
{
  ${Object.entries(categoriesForPrompt)
    .map(
      ([, label]) => `"${label}": ["liste de tags pertinents"]`
    )
    .join(",\n  ")}
}

Instructions spécifiques:
- Identifiez les références musicales (compositeurs, œuvres, instruments) si présentes
- Détectez les éléments visuels spécifiques (formats, dimensions, dispositifs)
- Repérez les références culturelles et les influences artistiques mentionnées
- Analysez la composition et la structure de l'œuvre
- Identifiez les lieux d'exposition et le contexte de présentation

Répondez uniquement avec le JSON sans autre commentaire. Incluez seulement les catégories qui ont au moins un tag pertinent. Limitez-vous à 5 tags maximum par catégorie, en privilégiant les plus pertinents. Tous les tags doivent être en français et en minuscules.
`;

      let result;

      if (apiEndpoint === "openai") {
        const response = await fetch(`${API_BASE_URL}/api/analyse-tags`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ description: text, client_id: profile?.id || null }),
        });

        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }

        const data = await response.json();
        if (!data || !data.tags) {
          throw new Error("Réponse API invalide (pas de champ 'tags')");
        }
        result = data.tags;
      } else if (apiEndpoint === "azure") {
        const response = await fetch(
          "https://your-azure-endpoint.openai.azure.com/openai/deployments/your-deployment-name/chat/completions?api-version=2023-05-15",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "api-key": apiKey,
            },
            body: JSON.stringify({
              messages: [{ role: "user", content: prompt }],
              temperature: 0.3,
              max_tokens: 600,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }

        const data = await response.json();
        const jsonContent = data.choices[0].message.content;
        result = JSON.parse(jsonContent);
      } else if (apiEndpoint === "mock") {
        // Mode mock → enrichit les résultats locaux
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const mockAnalysis = { ...localResults };
        const normalizedText = text.toLowerCase();

        if (
          !mockAnalysis[TAG_CATEGORIES.STYLE] &&
          (normalizedText.includes("contemporain") ||
            normalizedText.includes("2014") ||
            normalizedText.includes("2015") ||
            normalizedText.includes("2016"))
        ) {
          mockAnalysis[TAG_CATEGORIES.STYLE] = ["art contemporain"];
        }

        if (normalizedText.includes("haydn")) {
          if (!mockAnalysis[PREMIUM_CATEGORIES.INFLUENCE]) {
            mockAnalysis[PREMIUM_CATEGORIES.INFLUENCE] = [
              "musique classique",
              "joseph haydn",
            ];
          }
        }

        if (!mockAnalysis[TAG_CATEGORIES.ERA]) {
          if (normalizedText.includes("2014")) {
            mockAnalysis[TAG_CATEGORIES.ERA] = [
              "art du 21e siècle",
              "années 2010",
            ];
          }
        }

        if (isPremium && !mockAnalysis[PREMIUM_CATEGORIES.MARKET]) {
          mockAnalysis[PREMIUM_CATEGORIES.MARKET] = [
            "édition limitée",
            "photographie contemporaine",
          ];
        }

        if (
          !mockAnalysis[INTERDISCIPLINARY_CATEGORIES.CONCEPTUAL] &&
          (normalizedText.includes("inspire") ||
            normalizedText.includes("interprétation"))
        ) {
          mockAnalysis[INTERDISCIPLINARY_CATEGORIES.CONCEPTUAL] = [
            "interprétation visuelle",
            "transcription musicale",
          ];
        }

        result = mockAnalysis;
      }

      const mergedResults = { ...localResults, ...result };

      if (apiEndpoint !== "mock") {
        apiUsageManager.incrementUsageCount();
        setUsageStats(apiUsageManager.getUsageStats());
      }

      apiUsageManager.cacheResult(text, result);
      setSuggestedTags(mergedResults);
      setLastAnalyzedText(text);
      return mergedResults;
    } catch (err) {
      // On garde le détail technique en console (pour le debug), mais le client
      // ne voit qu'un message neutre — jamais un « Erreur API: 500 » ni un
      // message OpenAI (quota, clé…).
      console.error("Erreur lors de l'analyse de texte:", err);
      setError(
        "Les suggestions IA sont momentanément indisponibles. Réessayez plus tard ou contactez le support Artkiv (support@artkiv.com)."
      );
      setSuggestedTags(localResults);
      return localResults;
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Effets ----------

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!canUseAuto) return;
    if (!description || description.trim().length <= 20) return;

    const localResults = performLocalAnalysis(description);
    setSuggestedTags(localResults);
    setLocalAnalysisDone(true);

    if (
      apiEndpoint !== "mock" &&
      usageStats.remainingCalls < 2 &&
      description.length > 100
    ) {
      setShowManualAnalysisButton(true);
    } else {
      const timer = setTimeout(() => {
        analyzeWithAPI(description);
        setShowManualAnalysisButton(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, canCallApi, apiEndpoint, canUseAuto]);

  useEffect(() => {
    setSelectedTags(currentTags || []);
  }, [currentTags]);

  useEffect(() => {
    setUsageStats(apiUsageManager.getUsageStats());
  }, [isLoading]);

  // ---------- Gestion tags sélectionnés ----------

  const addTag = (tag) => {
    if (!selectedTags.includes(tag) && selectedTags.length < 10) {
      const newSelectedTags = [...selectedTags, tag];
      setSelectedTags(newSelectedTags);
      setTags(newSelectedTags);
    }
  };

  const removeTag = (tag) => {
    const newSelectedTags = selectedTags.filter((t) => t !== tag);
    setSelectedTags(newSelectedTags);
    setTags(newSelectedTags);
  };

  const handleManualAnalysis = () => {
    if (!canUseAuto) return;
    if (!canCallApi && apiEndpoint !== "mock") return;
    analyzeWithAPI(description, true);
    setShowManualAnalysisButton(false);
  };

  // ---------- Rendu ----------

  return (
    <div className="mt-6 space-y-4">
      {/* Message si l’auto n’est pas dispo */}
      {!canUseAuto && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          <div className="font-medium mb-1">Tags automatiques (IA)</div>
          <p>
            La génération automatique de tags est réservée aux plans{" "}
            <strong>Premium</strong> et <strong>Art Advisor</strong>. Vous
            pouvez continuer à gérer vos tags manuellement dans la section de
            catégorisation.
          </p>
        </div>
      )}

      {canUseAuto && (
        <>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Mots-clés suggérés par IA</h3>

            <div className="flex items-center text-sm text-gray-500">
              {isLoading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin h-4 w-4 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Analyse en cours...
                </span>
              ) : (
                <span>
                  {Object.keys(suggestedTags).length > 0
                    ? "Analyse complète"
                    : localAnalysisDone
                    ? "Analyse locale terminée"
                    : "En attente d'analyse"}
                </span>
              )}
            </div>
          </div>

          <ApiUsageStatus />

          {showManualAnalysisButton &&
            description &&
            description.length > 20 && (
              <div className="flex justify-center">
                <button
                  onClick={handleManualAnalysis}
                  disabled={!canCallApi && apiEndpoint !== "mock"}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    !canCallApi && apiEndpoint !== "mock"
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Analyse complète par IA
                </button>
              </div>
            )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
              <button
                className="ml-2 text-red-600 hover:text-red-800 underline"
                onClick={() => setError(null)}
              >
                Fermer
              </button>
            </div>
          )}

          {!canCallApi && apiEndpoint !== "mock" && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-sm">
              Vous avez atteint votre limite mensuelle d'analyses complètes.
              Passez au compte Premium pour continuer.
              <div className="mt-1 text-xs">
                Les résultats d'analyse locale sont toujours disponibles.
              </div>
            </div>
          )}

          {/* Tags organisés par catégorie */}
          <div className="space-y-4">
            {/* Interdisciplinaire */}
            {Object.keys(INTERDISCIPLINARY_CATEGORIES).map((key) => {
              const category = INTERDISCIPLINARY_CATEGORIES[key];
              const tags = suggestedTags[category] || [];

              if (tags.length === 0) return null;

              return (
                <div
                  key={category}
                  className="p-3 rounded-md bg-indigo-50 border border-indigo-200"
                >
                  <h4 className="font-medium text-sm mb-2 text-indigo-700">
                    {category}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(tags)].map((tag, index) => (
                      <button
                        key={`${tag}-${index}`}
                        onClick={() => addTag(tag)}
                        disabled={
                          selectedTags.includes(tag) ||
                          selectedTags.length >= 10
                        }
                        className={`px-2 py-1 rounded-full text-xs ${
                          selectedTags.includes(tag)
                            ? "bg-indigo-200 text-indigo-800 cursor-not-allowed"
                            : selectedTags.length >= 10
                            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "bg-white border border-indigo-300 hover:bg-indigo-100 text-indigo-700"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Standard */}
            {Object.entries(suggestedTags)
              .filter(
                ([category]) =>
                  !Object.values(INTERDISCIPLINARY_CATEGORIES).includes(
                    category
                  ) &&
                  !Object.values(PREMIUM_CATEGORIES).includes(category)
              )
              .map(([category, tags]) => {
                if (!tags || tags.length === 0) return null;

                return (
                  <div
                    key={category}
                    className="p-3 rounded-md bg-gray-50 border border-gray-200"
                  >
                    <h4 className="font-medium text-sm mb-2 text-gray-700">
                      {category}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {[...new Set(tags)].map((tag, index) => (
                        <button
                          key={`${tag}-${index}`}
                          onClick={() => addTag(tag)}
                          disabled={
                            selectedTags.includes(tag) ||
                            selectedTags.length >= 10
                          }
                          className={`px-2 py-1 rounded-full text-xs ${
                            selectedTags.includes(tag)
                              ? "bg-indigo-200 text-indigo-800 cursor-not-allowed"
                              : selectedTags.length >= 10
                              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                              : "bg-white border border-indigo-300 hover:bg-indigo-100 text-indigo-700"
                          }`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

            {/* Premium */}
            {Object.entries(suggestedTags)
              .filter(([category]) =>
                Object.values(PREMIUM_CATEGORIES).includes(category)
              )
              .map(([category, tags]) => {
                const isPremiumUser = apiUsageManager.isPremium();
                if (!tags || tags.length === 0 || !isPremiumUser) return null;

                return (
                  <div
                    key={category}
                    className="p-3 rounded-md bg-purple-50 border border-purple-200"
                  >
                    <h4 className="font-medium text-sm mb-2 text-purple-700">
                      {category}
                      <span className="ml-2 text-xs bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded-full">
                        Premium
                      </span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {[...new Set(tags)].map((tag, index) => (
                        <button
                          key={`${tag}-${index}`}
                          onClick={() => addTag(tag)}
                          disabled={
                            selectedTags.includes(tag) ||
                            selectedTags.length >= 10
                          }
                          className={`px-2 py-1 rounded-full text-xs ${
                            selectedTags.includes(tag)
                              ? "bg-indigo-200 text-indigo-800 cursor-not-allowed"
                              : selectedTags.length >= 10
                              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                              : "bg-white border border-indigo-300 hover:bg-indigo-100 text-indigo-700"
                          }`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

            {Object.keys(suggestedTags).length === 0 &&
              !isLoading &&
              description &&
              description.length > 20 && (
                <div className="p-3 bg-yellow-50 rounded-md text-yellow-700 text-sm">
                  Aucun tag suggéré.{" "}
                  {showManualAnalysisButton
                    ? "Cliquez sur « Analyse complète par IA »."
                    : "Essayez d'ajouter plus de détails à la description."}
                </div>
              )}
          </div>
        </>
      )}

      {/* Zone sélection + message Family Office */}
      <div className="mt-4">
        {isFamilyOffice && (
          <p className="mt-2 text-xs text-gray-500">
            Vos clients gèrent eux-mêmes la catégorisation.
            <br />
            Vous disposez d’une vue consolidée en lecture seule.
          </p>
        )}

        <h4 className="font-medium text-sm text-gray-700 mb-2">
          Tags sélectionnés ({selectedTags.length}/10)
        </h4>

        {selectedTags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag, index) => (
              <span
                key={`${tag}-${index}`}
                className="flex items-center gap-1 bg-gray-200 px-2 py-1 rounded-full text-xs"
              >
                {tag}
                {canEditManual && (
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="text-gray-500 hover:text-red-600 ml-1"
                    title="Supprimer"
                  >
                    ✕
                  </button>
                )}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 italic text-sm">
            Aucun tag sélectionné
          </p>
        )}
      </div>

      <div className="p-3 bg-blue-50 rounded-md text-blue-700 text-sm mt-4">
        <p className="font-medium">Conseils pour de meilleurs résultats :</p>
        <ul className="list-disc list-inside mt-1">
          <li>Incluez des détails techniques sur le médium et les matériaux</li>
          <li>
            Mentionnez toutes les influences ou références (musicales,
            littéraires, etc.)
          </li>
          <li>
            Précisez les dimensions, formats et caractéristiques visuelles
            importantes
          </li>
          <li>
            Indiquez le contexte d'exposition et les informations historiques
          </li>
          <li>Décrivez les thèmes et concepts abordés par l'œuvre</li>
        </ul>
      </div>
    </div>
  );
};

export default ApiTagsGenerator;
